/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { Link, useMatch, useParams, useLocation} from "react-router-dom";
import { SideProfileMenu } from "./SideMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPlus, faSliders, faSun, faMoon, faGlobe } from "@fortawesome/free-solid-svg-icons";
import { FepkContext } from "../../context/FepkContext";
import { useTranslation } from "react-i18next";
import { getUserById } from "../../api/users";
import LanguageToggle, { useLanguageToggle } from "../LanguageToggle";
import ThemeToggle from "../ThemeToggle";
import "./NavbarToggle.css";
import { useTheme } from "../../context/ThemeContext";

// Import the new Wizard!
import CreateEpkWizard from "../Dashboard/Epks/CreateEpkWizard/CreateEpkWizard";
import { useEpkWizard } from "../../context/EpkWizardContext";

import NavbarDropdown, { NavbarDropdownItem } from "./NavbarDropdown";
 
function NavbarButtons({ user, setToggle, toggle, ismobile = false }) {
  const { t } = useTranslation();
  const { fepkId, fepkMaker, epkCollaborators } = React.useContext(FepkContext);
  const [picture, setPicture] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const { id: profileId } = useParams();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const isLight = theme === "light";

  const { isOpen: isWizardOpen, openWizard, closeWizard } = useEpkWizard();

  useEffect(() => {
    if (user?.id) {
      getUserById(user.id)
        .then((res) => {
          setPicture(
            res.picture.startsWith("https")
              ? res.picture
              : `${process.env.REACT_APP_AWS_URL}/${res.picture}`
          );
        })
        .catch((err) => console.error("Error fetching user:", err));
    }
  }, [user, location.pathname]);

    // --- 1. PAGE TYPE DETECTION ---
  const isEpkViewPage = location.pathname.startsWith('/epk/');
  const isUserViewPage = location.pathname.startsWith('/user/');
  const isCurrentlyEditing = new URLSearchParams(location.search).get("edit") === "true";

  // EPK Permissions: User is the Maker OR is in the Collaborators array
  const isOwner = user?.id === fepkMaker?._id || user?.id === fepkMaker;
  const isCollaborator = epkCollaborators?.some(
    (c) => (c.user?._id || c.user || c) === user?.id
  );
  const canEditEPK = isEpkViewPage && (isOwner || isCollaborator) && fepkId;

  const canEditProfile = isUserViewPage && user?.id === profileId;

  // EPK button logic for mobile dropdown
  const showCreateEPK = isEpkViewPage && !isCollaborator;
  const showEditEPK = canEditEPK && !isCurrentlyEditing;
  const showBothEpkActions = showCreateEPK && showEditEPK;

  const { isEnglish, toggle: switchLanguage } = useLanguageToggle();

  return (
    <>
      {!user ? (
        <div className="tw-flex tw-items-center">
          <div className='tw-hidden md:tw-flex tw-items-center tw-gap-3'>
            <Link
              to='/login'
              className='tw-px-5 tw-py-2 tw-rounded-full tw-border tw-border-[#FF43A7]/60 tw-bg-transparent tw-text-sm tw-font-semibold tw-text-[#FF43A7] hover:tw-bg-[#FF43A7]/15 hover:tw-shadow-[0_0_12px_rgba(255,67,167,0.3)] tw-transition-all tw-no-underline'
            >
              {t("Login")}
            </Link>

            <Link
              to="/signup"
              className='tw-px-5 tw-py-2 tw-rounded-full tw-border tw-border-[#FF43A7]/60 tw-bg-[#FF43A7]/15 tw-text-sm tw-font-semibold tw-text-[#FF43A7] hover:tw-bg-[#FF43A7]/25 hover:tw-shadow-[0_0_12px_rgba(255,67,167,0.3)] tw-transition-all tw-no-underline'
            >
              {t("Register")}
            </Link>
            <ThemeToggle />
            <LanguageToggle />
          </div>

          {/* Mobile Hamburger Icon */}
          <div
            className='tw-flex tw-items-center md:tw-hidden'
            onClick={() => setToggle((prev) => !prev)}
          >
            <button
              type="button"
              aria-label={t("Toggle navigation menu")}
              aria-expanded={toggle}
              className={`toggle tw-mr-4 tw-bg-transparent ${toggle ? "activeNavItem" : ""}`}
            >
              <div className='bars' id='bar1'></div>
              <div className='bars' id='bar2'></div>
              <div className='bars' id='bar3'></div>
            </button>
          </div>
        </div>
      ) : (
        <div className='tw-flex tw-items-center tw-justify-center tw-gap-3 md:tw-gap-4 tw-p-2 md:tw-p-4'>
          {/* === DESKTOP (md+): existing full-size buttons === */}
          <div className='tw-hidden md:tw-flex tw-items-center tw-justify-center tw-gap-3 md:tw-gap-4'>

            {/* CREATE EPK BUTTON */}
            {isEpkViewPage && !isCollaborator && (
              <button
                onClick={openWizard}
                className="tw-flex tw-items-center tw-justify-center tw-h-12 tw-w-12 md:tw-w-auto md:tw-px-5 md:tw-gap-2 tw-rounded-full tw-border tw-border-[#FF43A7]/60 tw-bg-[#FF43A7]/15 tw-text-sm tw-font-semibold tw-text-[#FF43A7] hover:tw-bg-[#FF43A7]/25 hover:tw-shadow-[0_0_12px_rgba(255,67,167,0.3)] tw-transition-all tw-cursor-pointer tw-shrink-0"
              >
                <FontAwesomeIcon icon={faPlus} className="tw-text-lg" />
                <span className="tw-hidden md:tw-inline">{t("Create EPK")}</span>
              </button>
            )}

            {/* EDIT BUTTON (Dynamic for both EPK and User Profile) */}
            {(canEditEPK || canEditProfile) && !isCurrentlyEditing && (
              <Link
                to={`${location.pathname}?edit=true`}
                className="tw-flex tw-items-center tw-justify-center tw-h-12 tw-w-12 md:tw-w-auto md:tw-px-5 md:tw-gap-2 tw-rounded-full tw-border tw-border-[#FF43A7]/60 tw-bg-[#FF43A7]/15 tw-text-sm tw-font-semibold tw-text-[#FF43A7] hover:tw-bg-[#FF43A7]/25 hover:tw-shadow-[0_0_12px_rgba(255,67,167,0.3)] tw-transition-all tw-no-underline tw-shrink-0"
              >
                <FontAwesomeIcon icon={faPen} className="tw-text-lg" />
                <span className="tw-hidden md:tw-inline">
                  {isEpkViewPage ? t("Edit EPK") : t("Edit Profile")}
                </span>
              </Link>
            )}
            <ThemeToggle />
            <LanguageToggle />
          </div>

          {/* === MOBILE (below md): compact dropdowns === */}
          <div className="md:tw-hidden tw-flex tw-items-center tw-gap-2">

            {/* EPK dropdown: only when user can both edit and create */}
            {showBothEpkActions && (
              <NavbarDropdown icon={faPen} ariaLabel={t("EPK actions")}>
                <NavbarDropdownItem
                  icon={faPen}
                  label={t("Edit EPK")}
                  href={`${location.pathname}?edit=true`}
                />
                <NavbarDropdownItem
                  icon={faPlus}
                  label={t("Create EPK")}
                  onClick={openWizard}
                />
              </NavbarDropdown>
            )}

            {/* Single Edit button when only edit applies (no create) */}
            {!showBothEpkActions && (canEditEPK || canEditProfile) && !isCurrentlyEditing && (
              <Link
                to={`${location.pathname}?edit=true`}
                aria-label={isEpkViewPage ? t("Edit EPK") : t("Edit Profile")}
                className="tw-flex tw-items-center tw-justify-center tw-h-12 tw-w-12 tw-rounded-full tw-border tw-border-[#FF43A7]/60 tw-bg-[#FF43A7]/15 tw-text-[#FF43A7] tw-transition-all tw-no-underline tw-shrink-0"
              >
                <FontAwesomeIcon icon={faPen} className="tw-text-lg" />
              </Link>
            )}

            {/* Single Create button when only create applies (visitor, not collaborator) */}
            {!showBothEpkActions && showCreateEPK && !showEditEPK && (
              <button
                onClick={openWizard}
                aria-label={t("Create EPK")}
                className="tw-flex tw-items-center tw-justify-center tw-h-12 tw-w-12 tw-rounded-full tw-border tw-border-[#FF43A7]/60 tw-bg-[#FF43A7]/15 tw-text-[#FF43A7] tw-transition-all tw-cursor-pointer tw-shrink-0"
              >
                <FontAwesomeIcon icon={faPlus} className="tw-text-lg" />
              </button>
            )}

            {/* Preferences dropdown: replaces ThemeToggle + LanguageToggle */}
            <NavbarDropdown icon={faSliders} ariaLabel={t("Preferences")}>
              <NavbarDropdownItem
                icon={isLight ? faMoon : faSun}
                label={isLight ? t("Dark mode") : t("Light mode")}
                onClick={toggleTheme}
              />
              <NavbarDropdownItem
                icon={faGlobe}
                label={isEnglish ? t("French") : t("English")}
                onClick={switchLanguage}
              />
            </NavbarDropdown>
          </div>

          <div className={`tw-relative tw-inline-block tw-rounded-full ${ismobile ? 'tw-p-[1px] tw-bg-gradient-to-r tw-from-[#FF00A0] tw-to-[#1E0039]' : ''}`}>
            <img
              src={picture}
              alt={t('User Avatar')}
              onClick={() => setMenuOpen((prev) => !prev)}
              className='tw-h-12 tw-w-12 md:tw-h-16 md:tw-w-16 tw-rounded-full tw-object-cover tw-cursor-pointer'
            />
            <SideProfileMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
          </div>
        </div>
      )}

      {/* Modal stays hidden in the background until triggered */}
      <CreateEpkWizard isOpen={isWizardOpen} onClose={closeWizard} />
    </>
  );
}

export default NavbarButtons;
